import React, { useContext, useEffect } from 'react';
import useAsyncEffect from 'use-async-effect';
import Inputs from 'components/inputs/inputs';
import { Form } from 'react-bootstrap';
import BoardingContext from 'components/navigation/boarding/boardingContext';
import BoardingHelper from 'components/navigation/boarding/boardingHelper';
import BoardingService from 'services/boardingService';
import UserService from 'services/userService';

export default function BoardingUserInformation(props) {
  const { boardingState } = useContext(BoardingContext);
  const step = boardingState.step;

  if (step === BoardingHelper.enums.steps.UserEmailEntry) {
    return <UserEmailForm onUserSearch={props.onUserSearch} />
  }
  if (step === BoardingHelper.enums.steps.UserSearch) {
    return <UserSearch />;
  }
  if (step === BoardingHelper.enums.steps.UserDetails) {
    return <UserDetailsForm />;
  }
  if (step === BoardingHelper.enums.steps.UserFound) {
    return <UserFoundMessage />;
  }
  if (step === BoardingHelper.enums.steps.UserError) {
    return <UserErrorMessage />;
  }
  if (step === BoardingHelper.enums.steps.UserErrorReporting) {
    return <UserErrorReporting />;
  }

  return null;
}

function UserEmailForm(props) {
  const { boardingState, setUserSearch } = useContext(BoardingContext);

  return (
    <>
      <Form.Group>
        <Form.Label required>Email</Form.Label>
        <Inputs.Email required placeholder='Please enter user email' value={boardingState.user.search} onValueChange={setUserSearch} onEnterPress={props.onUserSearch} autoFocus='true' />
      </Form.Group>
    </>
  );
}

function UserSearch(props) {
  const { boardingState, setStep, updateUser } = useContext(BoardingContext);

  useAsyncEffect(async () => {
    try {
      const userDetails = await BoardingService.API.getUserDetails(boardingState.data.dealer?.id || -1, boardingState.user.search);
      if (userDetails) {
        updateUser({
          email: boardingState.user.search,
          id: userDetails.id,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          enterpriseId: userDetails.enterpriseId,
          enterpriseName: userDetails.enterpriseName,
        });
        setStep(BoardingHelper.enums.steps.UserFound);
      }
    }
    catch (error) {
      if (error.errorCode === 1) {
        //Sser not found
        updateUser({
          email: boardingState.user.search,
        });
        setStep(BoardingHelper.enums.steps.UserDetails);
      }
      else if (error.errorCode === 2) {
        //User beongs to another dealer
        setStep(BoardingHelper.enums.steps.UserError, {
          user: {
            search: boardingState.user.search,
            error: 'User belongs to another dealer',
          },
        });
      }
      else if (error.errorCode === 3) {
        //User is inactive
        setStep(BoardingHelper.enums.steps.UserError, {
          user: {
            search: boardingState.user.search,
            error: 'User is inactive',
          },
        });
      }
      else if (error.errorCode === 4) {
        //User is not a master user
        setStep(BoardingHelper.enums.steps.UserError, {
          user: {
            search: boardingState.user.search,
            error: 'User cannot be used as a master user',
          },
        });
      }
    }
  }, []);

  return (
    <div>Searching...</div>
  );
}

function UserDetailsForm(props) {
  const { boardingState, updateUser } = useContext(BoardingContext);
  const data = boardingState.data.user || {};

  const updateField = (field, value) => {
    let newData = { ...data };
    newData[field] = value;
    updateUser(newData);
  };

  return (
    <>
      <Form.Group>
        <Form.Label>Email</Form.Label>
        <Inputs.Plaintext value={data.email} />
      </Form.Group>
      <Form.Row>
        <Form.Group className='col-md-6'>
          <Form.Label required>First Name</Form.Label>
          <Inputs.Text required placeholder='Please enter first name' value={data.firstName} onValueChange={(v) => updateField('firstName', v)} />
        </Form.Group>
        <Form.Group className='col-md-6 mb-3'>
          <Form.Label required>Last Name</Form.Label>
          <Inputs.Text required placeholder='Please enter last name' value={data.lastName} onValueChange={(v) => updateField('lastName', v)} />
        </Form.Group>
      </Form.Row>
      <Form.Group>
        <Form.Label required>Enterprise Name</Form.Label>
        <Inputs.Text required placeholder='Please enter enterprise name' value={data.enterpriseName} onValueChange={(v) => updateField('enterpriseName', v)} />
      </Form.Group>
    </>
  );
}

function UserFoundMessage(props) {
  const { boardingState } = useContext(BoardingContext);
  const data = boardingState.data.user || {};

  return (
    <>
      <div className='alert alert-success'>
        <div className='alert-heading'>
          <i className='icons8-ok-2 align-middle' style={{ fontSize: 30 }} />
          <span className='h4 align-middle ml-2'>User Found</span>
        </div>
      </div>
      <div className='mb-2'>
        <div><div className='form-label'>Email</div></div>
        <div className='mb-2'>{data.email}</div>
        <div><div className='form-label'>Name</div></div>
        <div className='mb-2'>{data.firstName} {data.lastName}</div>
        <div><div className='form-label'>Enterprise</div></div>
        <div className='mb-2'>{data.enterpriseName}</div>
      </div>
    </>
  );
}

function UserErrorMessage(props) {
  const { boardingState } = useContext(BoardingContext);

  return (
    <>
      <div className='alert alert-danger'>
        <div className='alert-heading'>
          <i className='icons8-box-important align-middle' style={{ fontSize: 30 }} />
          <span className='h4 align-middle ml-2'>There was a problem</span>
        </div>
      </div>
      <div className='mb-2'>
        <div><div className='form-label'>Email</div></div>
        <div className='mb-2'>{boardingState.user.search}</div>
        <div><div className='form-label'>Error</div></div>
        <div className='mb-2'>{boardingState.user.error}</div>
      </div>
    </>
  );
}

function UserErrorReporting(props) {
  const { boardingState, updateProblem } = useContext(BoardingContext);
  const data = boardingState.problem || {};

  const updateField = (field, value) => {
    let newData = { ...data };
    newData[field] = value;
    updateProblem(newData);
  };

  useEffect(() => {
    updateProblem({
      email: data.email || UserService.getUserEmail(),
      details: `[Error]\nMessage: ${boardingState.user.error}\nStep: User Information\n\n[Entered Data]\nDealer Name: ${boardingState.data.dealer.name}\nDealer Id: ${boardingState.data.dealer.id}\nStore Name: ${boardingState.data.store.name}\nKey Number: ${boardingState.data.store.keyId}\nEmail: ${boardingState.user.search}`,
      comments: '',
    });
  }, []);

  return (
    <>
      <div className='mb-2'>
        <div><div className='form-label'>Problem Details</div></div>
        <div className='card card-light-default card-body mt-1 mb-3'>
          <pre className='m-0'>
            {data.details}
          </pre>
        </div>
        <Form.Group>
          <Form.Label>Report To</Form.Label>
          <Inputs.Plaintext value='POSitouch Cloud Support' />
        </Form.Group>
        <Form.Group>
          <Form.Label required>Report From</Form.Label>
          <Inputs.Text
            required
            placeholder='Please enter your email address'
            value={data.email}
            onValueChange={(v) => updateField('email', v)}
            help='We will respond to this email address with a resolution'
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Comments</Form.Label>
          <Inputs.TextArea placeholder='Please enter any additional comments' value={data.comments} onValueChange={(v) => updateField('comments', v)} />
        </Form.Group>
      </div>
    </>
  );
}